import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BookCard } from '../components/BookCard';
import { NewsletterSubscribeForm } from '../components/NewsletterSubscribeForm';
import { ArrowRight, BookOpen, Truck, Shield, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BANNER_IMAGE = 'https://customer-assets.emergentagent.com/job_7dada793-6fc9-4a53-aed3-591a2e9b89e3/artifacts/ljlgexe9_Diorrussell_banner.png';

export const HomePage = () => {
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, newRes, settingsRes] = await Promise.all([
          axios.get(`${API}/books?featured=true&limit=4`),
          axios.get(`${API}/books?new_release=true&limit=4`),
          axios.get(`${API}/settings/public`)
        ]);
        setFeaturedBooks(featuredRes.data.books || []);
        setNewReleases(newRes.data.books || []);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    axios.post(`${API}/seed`).catch(() => {});
    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-bottom bg-no-repeat"
          style={{ backgroundImage: `url(${BANNER_IMAGE})` }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="max-w-xl">
            <p className="font-accent text-xs uppercase tracking-[0.25em] text-primary/80 mb-5 animate-fade-in">
              Welcome to
            </p>
            <h1 className="font-script text-6xl sm:text-7xl lg:text-8xl leading-[1.1] mb-6 animate-fade-in animation-delay-100" style={{color: 'hsl(340, 30%, 25%)'}}>
              {settings.hero_title || 'Dior Russell'}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mb-8 animate-fade-in animation-delay-200 leading-relaxed max-w-md">
              {settings.hero_subtitle || 'Explore a world of captivating stories that inspire, empower, and transform.'}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in animation-delay-300">
              <Button asChild size="lg" className="btn-lift rounded-full px-8" data-testid="hero-browse-btn">
                <Link to="/browse">
                  Browse Collection
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="btn-lift rounded-full px-8 border-primary/30 hover:bg-primary/5" data-testid="hero-new-releases-btn">
                <Link to="/browse?new_release=true">
                  New Releases
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: BookOpen, title: 'Authentic Stories', desc: 'Original works by Dior Russell' },
              { icon: Truck, title: 'Fast Delivery', desc: 'Free shipping on orders over $30' },
              { icon: Shield, title: 'Secure Payment', desc: 'Multiple payment options' },
              { icon: Heart, title: 'Author Connect', desc: 'Direct support from the author' },
            ].map((feature, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-3 p-4">
                <div className="p-3.5 bg-primary/8 rounded-full">
                  <feature.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold mb-0.5">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Books */}
      {featuredBooks.length > 0 && (
        <section className="py-20 md:py-24" data-testid="featured-section">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-accent text-[11px] uppercase tracking-[0.25em] text-primary mb-2">Curated For You</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold">Featured Works</h2>
              </div>
              <Button asChild variant="ghost" className="hidden md:flex text-primary hover:text-primary hover:bg-primary/5 rounded-full">
                <Link to="/browse?featured=true">
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {featuredBooks.map((book) => (
                <BookCard key={book.book_id} book={book} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-secondary/40 to-secondary/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-accent text-[11px] uppercase tracking-[0.25em] text-primary mb-2">Stay Updated</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Stay Connected
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Be the first to know about new book releases, exclusive content, and special offers directly from Dior Russell.
              </p>
            </div>
            <Card className="border-border/50 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <NewsletterSubscribeForm />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* New Releases */}
      {newReleases.length > 0 && (
        <section className="py-20 md:py-24" data-testid="new-releases-section">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-accent text-[11px] uppercase tracking-[0.25em] text-primary mb-2">Just Arrived</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold">New Releases</h2>
              </div>
              <Button asChild variant="ghost" className="hidden md:flex text-primary hover:text-primary hover:bg-primary/5 rounded-full">
                <Link to="/browse?new_release=true">
                  View All
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {newReleases.map((book) => (
                <BookCard key={book.book_id} book={book} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="relative rounded-2xl p-12 md:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-200/80 via-pink-100/60 to-rose-50/40" />
            <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_7dada793-6fc9-4a53-aed3-591a2e9b89e3/artifacts/ljlgexe9_Diorrussell_banner.png')] bg-cover bg-center opacity-20" />
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4" style={{color: 'hsl(340, 30%, 25%)'}}>
                Join the Community
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto mb-8 leading-relaxed">
                Create an account to track your orders, build your personal library, and get exclusive updates.
              </p>
              <Button asChild size="lg" className="btn-lift rounded-full px-10 shadow-soft">
                <Link to="/register">Create Account</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
